const HTMLElementBase = globalThis.HTMLElement || class {};
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const CONFIG_SELECTOR = '[data-rosarium-bouquet-cart-preview-assets]';

export const BOUQUET_CART_PREVIEW_CONTRACT = 'rbcp2-native-lines-w1';
export const BOUQUET_CART_PREVIEW_MAX_LINES_LENGTH = 4096;
export const BOUQUET_CART_PREVIEW_VIEW_BOX = Object.freeze([0, 0, 420, 390]);

const PACKAGING_KEYS = Object.freeze(['kraft', 'cellophane', 'premium']);
const CONFIG_KEYS = Object.freeze(['assets', 'h', 'v', 'variants', 'viewBox', 'wrappers']);
const ASSET_KEYS = Object.freeze(['anchorX', 'anchorY', 'height', 'imageHeight', 'url', 'width']);
const VARIANT_KEYS = Object.freeze(['color', 'component']);
const WRAPPER_KEYS = Object.freeze(['frontUrl', 'height', 'rearUrl', 'width', 'x', 'y']);
const ASSET_KEY_PATTERN = /^[a-z0-9_-]+:[a-z0-9_-]+:[a-z0-9_-]+$/;
const IDENTITY_PATTERN = /^[a-z0-9_-]+$/;
const VARIANT_ID_PATTERN = /^[1-9][0-9]{8,19}$/;
const SAFE_ASSET_URL_PATTERN = /^(?:(?:https?:)?\/\/|\/[^/])/;
const FAMILY_VISUAL_SCALES = Object.freeze({
  eucalyptus: 1.05,
  pistachio: 1.05,
  lisianthus: 1.05,
  snapdragon: 1,
});

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(value, expectedKeys) {
  return isRecord(value) &&
    JSON.stringify(Object.keys(value).sort()) === JSON.stringify([...expectedKeys].sort());
}

function finiteInRange(value, minimum, maximum) {
  return Number.isFinite(value) && value >= minimum && value <= maximum;
}

function safeAssetUrl(value) {
  return typeof value === 'string' && value.length > 0 && value.length <= 4096 &&
    SAFE_ASSET_URL_PATTERN.test(value) && !/[\u0000-\u001f\u007f<>"'`\\]/.test(value);
}

function expectedImageHeight(assetKey) {
  const family = assetKey.split(':')[0];
  return family === 'eucalyptus' || family === 'pistachio' ? 158 : 166;
}

function assetKeysForVariant(config, variant) {
  const prefix = `${variant.component}:`;
  const suffix = `:${variant.color}`;
  return Object.keys(config.assets)
    .filter((assetKey) => assetKey.startsWith(prefix) && assetKey.endsWith(suffix))
    .sort();
}

function deterministicUnit(variantId, occurrence, salt) {
  let value = (Number(variantId) % 2147483647) ^ Math.imul(occurrence + 1, 2654435761) ^ salt;
  value = Math.imul(value ^ (value >>> 16), 2246822507);
  value = Math.imul(value ^ (value >>> 13), 3266489909);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
}

export function parseBouquetCartNativeLines(rawValue) {
  if (typeof rawValue !== 'string' || rawValue.length < 1 ||
    rawValue.length > BOUQUET_CART_PREVIEW_MAX_LINES_LENGTH) return null;
  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function parseBouquetCartPreviewConfig(rawValue) {
  if (typeof rawValue !== 'string' || rawValue.length < 1 || rawValue.length > 196608) return null;
  try {
    const parsed = JSON.parse(rawValue);
    return validateBouquetCartPreviewConfig(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function validateBouquetCartPreviewConfig(config) {
  if (!hasExactKeys(config, CONFIG_KEYS) || config.v !== 2 ||
    config.h !== BOUQUET_CART_PREVIEW_CONTRACT ||
    !Array.isArray(config.viewBox) || config.viewBox.length !== 4 ||
    config.viewBox.some((value, index) => value !== BOUQUET_CART_PREVIEW_VIEW_BOX[index]) ||
    !isRecord(config.assets) || Object.keys(config.assets).length < 1 ||
    !isRecord(config.variants) || Object.keys(config.variants).length < 1 ||
    Object.keys(config.variants).length > 101 ||
    !hasExactKeys(config.wrappers, PACKAGING_KEYS)) return false;

  for (const [assetKey, asset] of Object.entries(config.assets)) {
    if (!ASSET_KEY_PATTERN.test(assetKey) || !hasExactKeys(asset, ASSET_KEYS) ||
      !safeAssetUrl(asset.url) || !Number.isInteger(asset.width) ||
      !finiteInRange(asset.width, 1, 2048) || !Number.isInteger(asset.height) ||
      !finiteInRange(asset.height, 1, 2048) ||
      !finiteInRange(asset.anchorX, 0, 1) || !finiteInRange(asset.anchorY, 0, 1) ||
      asset.imageHeight !== expectedImageHeight(assetKey)) return false;
  }

  for (const [variantId, variant] of Object.entries(config.variants)) {
    if (!VARIANT_ID_PATTERN.test(variantId) || !hasExactKeys(variant, VARIANT_KEYS) ||
      !IDENTITY_PATTERN.test(variant.component) || !IDENTITY_PATTERN.test(variant.color) ||
      assetKeysForVariant(config, variant).length < 1) return false;
  }

  for (const packagingKey of PACKAGING_KEYS) {
    const wrapper = config.wrappers[packagingKey];
    if (!hasExactKeys(wrapper, WRAPPER_KEYS) || !safeAssetUrl(wrapper.rearUrl) ||
      !safeAssetUrl(wrapper.frontUrl) || !finiteInRange(wrapper.x, -100, 520) ||
      !finiteInRange(wrapper.y, -100, 520) || !finiteInRange(wrapper.width, 1, 600) ||
      !finiteInRange(wrapper.height, 1, 600)) return false;
  }
  return true;
}

export function validateBouquetCartNativeLines(lines, config, expectedStemCount = null) {
  if (!validateBouquetCartPreviewConfig(config) || !Array.isArray(lines) ||
    lines.length < 1 || lines.length > Object.keys(config.variants).length) return false;

  const seen = new Set();
  let stemCount = 0;
  for (const row of lines) {
    if (!Array.isArray(row) || row.length !== 2 || !Number.isInteger(row[0]) ||
      !Number.isInteger(row[1]) || row[1] < 1 || row[1] > 15) return false;
    const variantId = String(row[0]);
    if (seen.has(variantId) || !Object.prototype.hasOwnProperty.call(config.variants, variantId)) return false;
    seen.add(variantId);
    stemCount += row[1];
  }

  return stemCount >= 1 && stemCount <= 101 &&
    (expectedStemCount === null ||
      (Number.isInteger(expectedStemCount) && expectedStemCount === stemCount));
}

function expandNativeLines(lines, config) {
  const expanded = [];
  const maximumQuantity = Math.max(...lines.map((row) => row[1]));
  for (let occurrence = 0; occurrence < maximumQuantity; occurrence += 1) {
    for (const [variantId, quantity] of lines) {
      if (occurrence >= quantity) continue;
      const variant = config.variants[String(variantId)];
      const assetKeys = assetKeysForVariant(config, variant);
      const assetIndex = Math.floor(deterministicUnit(variantId, occurrence, 0x51ed270b) * assetKeys.length) % assetKeys.length;
      expanded.push({
        variantId,
        occurrence,
        variant,
        assetKey: assetKeys[assetIndex],
      });
    }
  }
  return expanded;
}

export function buildBouquetCartPreviewRenderModel(
  lines,
  config,
  packaging = '',
  expectedStemCount = null,
) {
  if (!PACKAGING_KEYS.includes(packaging) ||
    !validateBouquetCartNativeLines(lines, config, expectedStemCount)) return null;

  const expanded = expandNativeLines(lines, config);
  const total = expanded.length;
  const spread = total === 1 ? 0 : Math.min(32, 18 + total * 1.2);
  const stems = expanded.map((stem, index) => {
    const centered = total === 1 ? 0 : ((index / (total - 1)) * 2) - 1;
    const horizontalJitter = deterministicUnit(stem.variantId, stem.occurrence, 0x19a4c116) - 0.5;
    const depth = deterministicUnit(stem.variantId, stem.occurrence, 0x7f4a7c15);
    const asset = config.assets[stem.assetKey];
    const imageWidth = asset.imageHeight * (asset.width / asset.height);
    return {
      assetKey: stem.assetKey,
      baseX: 210 + horizontalJitter * 5,
      baseY: 342 + (depth - 0.5) * 2,
      rotation: centered * spread + horizontalJitter * 5,
      fixedScale: 0.84 + depth * 0.23,
      visualScale: FAMILY_VISUAL_SCALES[stem.variant.component] || 1.25,
      depth,
      image: {
        url: asset.url,
        x: -asset.anchorX * imageWidth,
        y: -asset.anchorY * asset.imageHeight,
        width: imageWidth,
        height: asset.imageHeight,
      },
    };
  }).sort((left, right) => left.depth - right.depth || left.rotation - right.rotation);

  const wrapper = config.wrappers[packaging];
  return {
    viewBox: [...config.viewBox],
    packaging,
    rear: {
      url: wrapper.rearUrl,
      x: wrapper.x,
      y: wrapper.y,
      width: wrapper.width,
      height: wrapper.height,
    },
    stems,
    front: {
      url: wrapper.frontUrl,
      x: wrapper.x,
      y: wrapper.y,
      width: wrapper.width,
      height: wrapper.height,
    },
  };
}

export function resolveBouquetCartPreviewConfigNode(host) {
  const localScope = host?.closest?.('cart-items-component');
  return localScope?.querySelector?.(CONFIG_SELECTOR) ||
    host?.ownerDocument?.querySelector?.(CONFIG_SELECTOR) ||
    null;
}

function setSvgAttributes(element, attributes) {
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, String(value));
  }
  return element;
}

export class RosariumBouquetCartPreview extends HTMLElementBase {
  static observedAttributes = [
    'data-lines',
    'data-packaging',
    'data-stem-count',
    'data-preview-state',
  ];

  connectedCallback() {
    this.initialized = true;
    this.schedulePreviewRender();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.initialized || oldValue === newValue || this.previewStateWrite) return;
    if (name === 'data-preview-state' && newValue !== 'fallback') return;
    this.schedulePreviewRender();
  }

  schedulePreviewRender() {
    if (this.previewRenderScheduled) return;
    this.previewRenderScheduled = true;
    queueMicrotask(() => {
      this.previewRenderScheduled = false;
      if (this.isConnected === false) return;
      this.renderPreview();
    });
  }

  setPreviewState(state) {
    this.previewStateWrite = true;
    try {
      this.setAttribute('data-preview-state', state);
    } finally {
      this.previewStateWrite = false;
    }
  }

  renderPreview() {
    if (this.previewRendering) return;
    this.previewRendering = true;

    try {
      const ownerDocument = this.ownerDocument;
      const configNode = resolveBouquetCartPreviewConfigNode(this);
      const linesRaw = this.getAttribute('data-lines') || '';
      const packaging = this.getAttribute('data-packaging') || '';
      const stemCountRaw = this.getAttribute('data-stem-count');
      const stemCount = stemCountRaw === null ? NaN : Number(stemCountRaw);
      if (!ownerDocument || !configNode || !Number.isInteger(stemCount) ||
        stemCount < 1 || stemCount > 101) {
        this.restoreFallback();
        return;
      }

      const config = parseBouquetCartPreviewConfig(configNode.textContent || '');
      const lines = parseBouquetCartNativeLines(linesRaw);
      const model = buildBouquetCartPreviewRenderModel(lines, config, packaging, stemCount);
      if (!model) {
        this.restoreFallback();
        return;
      }

      const signature = `${linesRaw}\n${packaging}\n${stemCountRaw}`;
      const existingSvg = this.firstElementChild?.classList?.contains('cart-items__bouquet-preview-svg')
        ? this.firstElementChild
        : null;
      if (existingSvg && this.previewSignature === signature) {
        this.previewSvg = existingSvg;
        this.setPreviewState('ready');
        return;
      }

      const svg = this.createPreviewSvg(ownerDocument, model);
      if (!svg) {
        this.restoreFallback();
        return;
      }

      if (!existingSvg) {
        const fallback = ownerDocument.createDocumentFragment();
        while (this.firstChild) fallback.append(this.firstChild);
        if (fallback.childNodes.length > 0) this.fallbackContent = fallback;
      }

      this.replaceChildren(svg);
      this.previewSvg = svg;
      this.previewSignature = signature;
      this.setPreviewState('ready');
    } catch {
      this.restoreFallback();
    } finally {
      this.previewRendering = false;
    }
  }

  createPreviewSvg(ownerDocument, model) {
    const svg = setSvgAttributes(ownerDocument.createElementNS(SVG_NAMESPACE, 'svg'), {
      class: 'cart-items__bouquet-preview-svg',
      viewBox: model.viewBox.join(' '),
      width: '100%',
      height: '100%',
      preserveAspectRatio: 'xMidYMid meet',
      focusable: 'false',
      'aria-hidden': 'true',
    });
    const onAssetError = () => this.restoreFallback();
    svg.append(this.createSvgImage(ownerDocument, model.rear, 'cart-items__bouquet-preview-wrapper cart-items__bouquet-preview-wrapper--rear', onAssetError));

    const stemLayer = setSvgAttributes(ownerDocument.createElementNS(SVG_NAMESPACE, 'g'), {
      class: 'cart-items__bouquet-preview-stems',
      'data-preview-layer': 'stems',
    });
    for (const stem of model.stems) {
      const group = setSvgAttributes(ownerDocument.createElementNS(SVG_NAMESPACE, 'g'), {
        class: 'cart-items__bouquet-preview-stem',
        transform: `translate(${stem.baseX} ${stem.baseY}) rotate(${stem.rotation}) scale(${stem.fixedScale})`,
      });
      const image = this.createSvgImage(ownerDocument, stem.image, 'cart-items__bouquet-preview-botanical', onAssetError);
      image.setAttribute('transform', `scale(${stem.visualScale})`);
      group.append(image);
      stemLayer.append(group);
    }
    svg.append(stemLayer);
    svg.append(this.createSvgImage(ownerDocument, model.front, 'cart-items__bouquet-preview-wrapper cart-items__bouquet-preview-wrapper--front', onAssetError));
    return svg;
  }

  createSvgImage(ownerDocument, model, className, onAssetError) {
    const image = setSvgAttributes(ownerDocument.createElementNS(SVG_NAMESPACE, 'image'), {
      class: className,
      href: model.url,
      x: model.x,
      y: model.y,
      width: model.width,
      height: model.height,
      preserveAspectRatio: 'xMidYMid meet',
    });
    image.addEventListener('error', onAssetError, { once: true });
    return image;
  }

  restoreFallback() {
    if (this.fallbackContent?.childNodes?.length > 0) {
      this.replaceChildren(this.fallbackContent);
      this.fallbackContent = null;
    } else {
      this.previewSvg?.remove();
    }
    this.previewSvg = null;
    this.previewSignature = '';
    this.setPreviewState?.('fallback');
  }
}

if (globalThis.customElements && globalThis.HTMLElement &&
  !globalThis.customElements.get('rosarium-bouquet-cart-preview')) {
  globalThis.customElements.define('rosarium-bouquet-cart-preview', RosariumBouquetCartPreview);
}

